import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Radio,
  Stack,
  styled,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { COLORS } from "../../../constants/colors";
import { postDashboard } from "../../../api/dashboard";
import UserContext from "../../../context/UserContext";
import { getSortedDashboards } from "../dashboardUtil";
import { useTranslation } from "react-i18next";
import localizationKeys from "../../../i18n/localizationKeys";
import { DASHBOARD_MODE } from "../constants";

const CreateDashboardTextField = styled(TextField)({
  "& label.Mui-focused": {
    color: "#A0AAB4",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B2BAC2",
  },
  "& .MuiOutlinedInput-root": {
    color: "white",
    "& fieldset": {
      borderColor: "#2c3a48",
    },
    "&:hover fieldset": {
      borderColor: "#B2BAC2",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6F7E8C",
    },
  },
  "& .MuiFormLabel-root": {
    color: "rgba(255, 255, 255, 0.6)",
  },
});

// for now, it's almost same as settings page but separate from settings page to keep flex
export const CreateDashboardPage = (props) => {
  const { currentVessel, setMode, setDashboards, setSelectedDashboard } = props;
  const { t } = useTranslation();
  const userData = useContext(UserContext).user;
  const vesselId = currentVessel.id;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false); //shareLevel 0 is private, 1 is public
  const [isEditableByOthers, setIsEditableByOthers] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleRadioChange = (e) => {
    if (e.target.value === "true") {
      setIsPublic(true);
    } else {
      setIsPublic(false);
    }
  };

  const handleToggleSwitch = (e) => {
    setIsEditableByOthers(e.target.checked);
  };

  const handleCreateNewDashboard = async (e) => {
    setIsCreating(true);
    const response = await postDashboard(currentVessel.id, {
      vesselId,
      ownerId: userData.id,
      name: title,
      description,
      shareLevel: isPublic ? 1 : 0,
      isEditableByOthers: isPublic && isEditableByOthers ? 1 : 0,
      items: [],
    });
    const sortedDashboards = await getSortedDashboards(currentVessel.id);
    setMode(DASHBOARD_MODE.EDIT);
    setDashboards(sortedDashboards);
    setSelectedDashboard(
      sortedDashboards.find((dashboard) => dashboard.id === response.postDashboard.id)
    );
    setIsCreating(false);
  };

  return (
    <Container>
      <Stack spacing={6}>
        <Stack spacing={6}>
          <Typography variant="h4" sx={{ color: "white" }}>
            {t(localizationKeys.DashboardNewDashboardSettings)}
          </Typography>
          <Stack sx={{ width: "40%" }} spacing={2}>
            <CreateDashboardTextField
              onChange={(e) => setTitle(e.target.value)}
              label={t(localizationKeys.DashboardNameFormText)}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: 32 } }}
              sx={{ color: "white" }}
              required
            ></CreateDashboardTextField>
            <CreateDashboardTextField
              onBlur={(e) => setDescription(e.target.value)}
              label={t(localizationKeys.DashboardDescriptionFormText)}
              variant="outlined"
              slotProps={{ htmlInput: { maxLength: 32 } }}
              sx={{ color: "white" }}
            ></CreateDashboardTextField>
          </Stack>
          <Stack direction={"row"} spacing={6}>
            <Stack spacing={2}>
              <Box component={"div"} sx={{ display: "flex", alignItems: "center" }}>
                <Radio
                  sx={{ color: "#ffffff4c", "&.Mui-checked": { color: COLORS.greenishBlue } }}
                  value={false}
                  checked={!isPublic}
                  onChange={handleRadioChange}
                />
                <Typography sx={{ color: "white" }}>
                  {t(localizationKeys.DashboardSettingsPrivate)}
                </Typography>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255, 0.6)" }}>
                {t(localizationKeys.DashboardSettingsPrivateSubText)}
              </Typography>
            </Stack>
            <Stack spacing={2} style={{ color: "white" }}>
              <Box component={"div"} sx={{ display: "flex", alignItems: "center" }}>
                <Radio
                  sx={{ color: "#ffffff4c", "&.Mui-checked": { color: COLORS.greenishBlue } }}
                  value={true}
                  checked={isPublic}
                  onChange={handleRadioChange}
                />
                <Typography sx={{ color: "white" }}>
                  {t(localizationKeys.DashboardSettingsPublic)}
                </Typography>
              </Box>
              <Typography sx={{ color: "rgba(255,255,255, 0.6)" }}>
                {t(localizationKeys.DashboardSettingsPublicSubText)}
              </Typography>
              {isPublic && (
                <Box>
                  <FormControlLabel
                    control={
                      // rectangle toggle switch
                      <Switch
                        checked={isEditableByOthers}
                        onChange={handleToggleSwitch}
                        sx={{
                          ".MuiSwitch-switchBase.Mui-checked": {
                            color: "white",
                            "+ .MuiSwitch-track": { backgroundColor: COLORS.greenishBlue },
                          },
                        }}
                        slotProps={{
                          track: {
                            sx: { backgroundColor: "rgba(255, 255, 255, 0.3)", borderRadius: 0 },
                          },
                          thumb: { sx: { borderRadius: 0, width: "12px", height: "12px" } },
                          switchBase: { sx: { padding: "13px" } },
                        }}
                      />
                    }
                    label={t(localizationKeys.DashboardSettingsEditableByOthers)}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
        <Box component={"div"} sx={{ display: "flex", justifyContent: "end" }}>
          <Button
            onClick={async () => {
              await handleCreateNewDashboard();
            }}
            variant="contained"
            sx={{
              color: "black",
              background: COLORS.greenishBlue,
              textTransform: "none",
              "&.Mui-disabled": { backgroundColor: "#28b6c7" },
            }}
            disabled={isCreating || title.length === 0}
          >
            {t(localizationKeys.DashboardSettingsCreateButtonText)}
          </Button>
        </Box>
      </Stack>
    </Container>
  );
};

CreateDashboardPage.propTypes = {
  currentVessel: PropTypes.object,
  setMode: PropTypes.func,
  isNoDashboard: PropTypes.bool,
  setDashboards: PropTypes.func,
  setSelectedDashboard: PropTypes.func,
};
